import { captureMessage } from '@sentry/nextjs';
import {
  APQ_GET_HEADERS,
  buildPersistedQueryUrl,
  getPersistedQueryExtension,
  resolveDocument,
  shouldRetryWithDocument,
} from './apq';
import { GraphQLError } from './errors';
import { defaultUrl, GraphQLRequestProps } from './graphql-request';

const getGraphqlRequestName = (document: GraphQLRequestProps['document']) => {
  return /^.*(?:query|mutation) ([a-zA-Z]+)[( {].*$/m.exec(document.toString())?.[1];
};
export const requestServer = async <Data = any>(
  props: Omit<GraphQLRequestProps, 'locale' | 'idToken'>,
  { tags = [], revalidate }: NextFetchRequestConfig = {},
) => {
  const documentName = getGraphqlRequestName(props.document);
  const requestId = `request-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  console.info(`Performing GraphQL request ${documentName}`, props.variables, {
    tags,
    revalidate,
    requestId,
  });

  const { query, operationName, isMutation } = resolveDocument(props.document);
  const extensions =
    process.env.NODE_ENV === 'development' ? undefined : await getPersistedQueryExtension(query);

  const next = {
    revalidate,
    tags: [
      ...tags,
      `${documentName}-${JSON.stringify(props.variables ?? {}).slice(0, 150)}`,
      requestId,
    ],
  };

  const get = (url: string) =>
    fetch(url, {
      method: 'GET',
      headers: { ...((props.requestHeaders ?? {}) as any), ...APQ_GET_HEADERS },
      next,
    });

  const post = (body: Record<string, unknown>) =>
    fetch(defaultUrl as string, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { ...((props.requestHeaders ?? {}) as any), 'Content-Type': 'application/json' },
      next,
    });

  const persistedUrl = extensions
    ? buildPersistedQueryUrl(defaultUrl as string, {
        operationName,
        variables: props.variables,
        extensions,
        isMutation,
      })
    : null;

  // Falls back to POST for mutations and over-length URLs, which still send the
  // hash alone; only a document we could not hash at all travels in full.
  const postBody = extensions
    ? { operationName, variables: props.variables, extensions }
    : { query, operationName, variables: props.variables };

  // Hashed queries go out as a cacheable GET carrying no document.
  let response = persistedUrl ? await get(persistedUrl) : await post(postBody);
  const postWithDocument = () =>
    post({ query, operationName, variables: props.variables, extensions });

  // A non-OK GET (e.g. 404 from the API when the hash isn't recognised) or a
  // bodiless response (e.g. 204) won't contain usable GraphQL data. Fall back
  // to POST with the full document before attempting to parse the body.
  let retriedWithPost = false;
  if (persistedUrl && !response.ok) {
    response = await postWithDocument();
    retriedWithPost = true;
  }

  let data: any;
  try {
    data = await response.json();
  } catch {
    if (persistedUrl && !retriedWithPost) {
      response = await postWithDocument();
      retriedWithPost = true;
      try {
        data = await response.json();
      } catch {
        throw new GraphQLError(
          `GraphQL request ${documentName} POST fallback returned non-JSON response (status ${response.status})`,
          response.status,
        );
      }
    } else {
      throw new GraphQLError(
        `GraphQL request ${documentName} returned non-JSON response (status ${response.status})`,
        response.status,
      );
    }
  }

  // Cache miss: register the document once over POST, then every later request
  // for it is a bare GET.
  if (!retriedWithPost && extensions && shouldRetryWithDocument(data?.errors)) {
    response = await postWithDocument();
    try {
      data = await response.json();
    } catch {
      throw new GraphQLError(
        `GraphQL request ${documentName} APQ registration POST returned non-JSON response (status ${response.status})`,
        response.status,
      );
    }
  }

  if (data.errors) {
    const contentDoesNotExist = data.errors.some((error: { message: string }) =>
      error?.message.includes('Content does not exist'),
    );

    if (!data.data || contentDoesNotExist) {
      const message = data?.errors?.[0]?.message ?? 'No message provided';
      const code = contentDoesNotExist ? 404 : (data?.errors?.[0]?.code ?? 500);

      throw new GraphQLError(message, code);
    }

    captureMessage('uncaught graphql error', {
      level: 'error',
      extra: {
        document: props.document,
        variables: props.variables,
        errorMessage: data.errors,
      },
    });
  }

  return data.data as Data;
};
